import { useState, useEffect } from 'react';
import { X, Edit3, MapPin, Globe, Save, AlertCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const EditVehicleModal = ({ isOpen, onClose, vehicle, onVehicleUpdated }) => {
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();
  const { getAuthHeaders } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    type: '',
    year: '',
    mileage: '',
    regionId: '',
    baseId: '',
  });

  // UI state
  const [countries, setCountries] = useState([]);
  const [bases, setBases] = useState([]);
  const [filteredBases, setFilteredBases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Vehicle type options
  const vehicleTypes = ['MRAP', 'HMMWV', 'JLTV', 'M113'];



  const fetchCountriesAndBases = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
      
      const basesResponse = await fetch(`${apiUrl}/api/fleet/bases`, { headers: getAuthHeaders() });
      if (!basesResponse.ok) {
        throw new Error('Failed to fetch bases');
      }
      const basesData = await basesResponse.json();
      setBases(basesData);

      const uniqueCountries = basesData.reduce((acc, base) => {
        if (!acc.find(country => country.id === base.countryId)) {
          acc.push({
            id: base.countryId,
            name: base.countryName
          });
        }
        return acc;
      }, []);
      
      setCountries(uniqueCountries);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load regions and bases. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError('');
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
      
      const updateData = {
        ...formData
      };

      const response = await fetch(`${apiUrl}/api/fleet/vehicles/${vehicle.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error('Failed to update vehicle');
      }

      const result = await response.json();
      onVehicleUpdated(result.vehicle);
      onClose();
    } catch (error) {
      console.error('Error updating vehicle:', error);
      setError('Failed to update vehicle. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Initialize form when modal opens or vehicle changes
  useEffect(() => {
    if (isOpen && vehicle) {
      fetchCountriesAndBases();
      initializeForm();
    }
  }, [isOpen, vehicle]);

  // Filter bases when region changes
  useEffect(() => {
    if (formData.regionId) {
      const filtered = bases.filter(base => base.countryId === formData.regionId);
      setFilteredBases(filtered);
      if (formData.baseId) {
        const baseStillValid = filtered.some(base => base.id === formData.baseId);
        if (!baseStillValid) {
          setFormData(prev => ({ ...prev, baseId: '' }));
        }
      }
    } else {
      setFilteredBases([]);
      setFormData(prev => ({ ...prev, baseId: '' }));
    }
  }, [formData.regionId, bases]);

  const initializeForm = () => {
    if (!vehicle) return;
    
    setFormData({
      type: vehicle.type || '',
      year: vehicle.year || '',
      mileage: vehicle.mileage || '',
      regionId: vehicle.regionId || '',
      baseId: vehicle.baseId || '',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`${themeClasses.primary} border ${themeClasses.border} rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${themeClasses.border}`}>
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-green-600 to-green-700 p-2 rounded-lg">
              <Edit3 size={20} className="text-white" />
            </div>
            <h2 className={`text-xl font-bold ${themeClasses.textPrimary}`}>Edit Vehicle - {vehicle?.model}</h2>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg ${themeClasses.textMuted} hover:${themeClasses.textPrimary} ${themeClasses.buttonSecondary} transition-colors`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
              <AlertCircle size={16} className="text-red-600 dark:text-red-400" />
              <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
            </div>
          )}

          {/* Vehicle Information Section */}
          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-4`}>
            <h3 className={`text-lg font-bold ${themeClasses.textPrimary} mb-4`}>Vehicle Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Vehicle Type */}
              <div>
                <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
                  Vehicle Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                  required
                >
                  <option value="">Select Type</option>
                  {vehicleTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
                  Year *
                </label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', e.target.value)}
                  placeholder="Enter vehicle year"
                  min="1990"
                  max={new Date().getFullYear() + 1}
                  className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none`}
                  required
                />
              </div>

              {/* Mileage */}
              <div>
                <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
                  Current Mileage *
                </label>
                <input
                  type="number"
                  value={formData.mileage}
                  onChange={(e) => handleInputChange('mileage', e.target.value)}
                  placeholder="Enter current mileage"
                  min="0"
                  className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none`}
                  required
                />
              </div>

              {/* Region Selection */}
              <div>
                <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
                  <Globe className="inline mr-1" size={14} />
                  Region *
                </label>
                <select
                  value={formData.regionId}
                  onChange={(e) => handleInputChange('regionId', e.target.value)}
                  disabled={loading}
                  className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none disabled:opacity-50`}
                  required
                >
                  <option value="">
                    {loading ? 'Loading regions...' : 'Select Region'}
                  </option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Base Selection */}
              <div>
                <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>
                  <MapPin className="inline mr-1" size={14} />
                  Base *
                </label>
                <select
                  value={formData.baseId}
                  onChange={(e) => handleInputChange('baseId', e.target.value)}
                  disabled={loading || !formData.regionId}
                  className={`w-full px-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none disabled:opacity-50`}
                  required
                >
                  <option value="">
                    {loading ? 'Loading bases...' : 
                     !formData.regionId ? 'Select region first' : 
                     filteredBases.length === 0 ? 'No bases available' : 'Select Base'}
                  </option>
                  {filteredBases.map(base => (
                    <option key={base.id} value={base.id}>
                      {base.name} - {base.location}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>





          {/* Form Actions */}
          <div className={`flex gap-3 pt-4 border-t ${themeClasses.border}`}>
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-6 py-3 ${themeClasses.buttonSecondary} rounded-lg transition-colors`}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || loading}
              className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Update Vehicle
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditVehicleModal;
